import { marked } from 'marked'
import { ImageTag, Reference } from '../../types/chatTypes'

export const convertYoutubeUrlToEmbed = (url: string): string => {
  // Check if it's already an embed URL
  if (url.includes('youtube.com/embed/')) {
    return `<iframe width="300" height="169" src="${url}" frameborder="0" allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture" allowfullscreen></iframe>`
  }

  // Convert standard YouTube URLs to embed format
  const youtubeRegex = /(?:https?:\/\/)?(?:www\.)?(?:youtube\.com\/watch\?v=|youtu\.be\/)([a-zA-Z0-9_-]+)/
  const match = url.match(youtubeRegex)

  if (match && match[1]) {
    const embedUrl = `https://www.youtube.com/embed/${match[1]}`
    return `<iframe width="300" height="169" src="${embedUrl}" frameborder="0" allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture" allowfullscreen></iframe>`
  }

  return url
}

export const isYoutubeLink = (url: string): boolean => {
  return url.includes('youtube.com/embed/') || url.includes('youtube.com/watch') || url.includes('youtu.be/')
}

export const isSharePointLink = (url: string): boolean => {
  return url.includes('sharepoint.com') || url.includes('.sharepoint.') || url.includes('office.com')
}

export const parseMarkdownToHTML = (markdownText: string): string => {
  const parsedMarkdownToHTML = marked.parse(markdownText, {}) as string

  const fragments = parsedMarkdownToHTML.split(/\[([^\]]+)\]/g)
  let processedHtml = fragments.join('')

  // Process any YouTube links in the HTML
  processedHtml = processedHtml.replace(/<a href="([^"]+)"[^>]*>([^<]+)<\/a>/g, (match, url, text) => {
    if (isYoutubeLink(url)) {
      return convertYoutubeUrlToEmbed(url)
    }
    return match
  })

  return processedHtml
}

export const parseCustomMarkdown = (markdownText: string): string => {
  let parsedMarkdown = marked.parseInline(markdownText, {}) as string
  parsedMarkdown = parsedMarkdown.replace('-', '&#9;-')

  return parsedMarkdown
}

export const parseUrlToHtml = (url: string, text: string) => {
  return `<a href="${url}" style="color: blue; text-decoration: underline;" target="_blank">${text}</a>`
}

const extractTitleFromUrl = (url: string): string => {
  try {
    const decodedUrl = decodeURIComponent(url)

    const lastPart = decodedUrl.split('/').pop() || ''

    const pathWithoutQuery = lastPart.split('?')[0]

    const titleWithoutExtension = pathWithoutQuery.split('.').slice(0, -1).join('.')

    return titleWithoutExtension || pathWithoutQuery
  } catch (error) {
    console.error('Error extracting title from URL:', error)
    return 'Untitled'
  }
}

// Inline citation markers the backend emits, e.g. `[S1]` — optional leading
// space captured so dropped markers don't leave a double space behind.
const INLINE_CITATION_RE = / ?\[(S\d+)\]/g

/**
 * Resolve inline `[S#]` citation markers in answer text into numbered markdown
 * links (`[1](url)`, `[2](url)`, …) using the references map (each carries the
 * `id` the backend tagged it with).
 *
 * - Numbers are assigned by order of first appearance and are stable per
 *   source (a source cited twice keeps its number). This matches the backend
 *   resolver used for persisted answers, so live and reloaded answers agree.
 * - A marker whose id matches a reference becomes `[n](url)`; `AnswerText`
 *   renders these numeric links as compact `[n]` citation chips.
 * - A marker with no matching reference (or a reference without a URL), and any
 *   marker seen before the references arrive, is dropped along with a single
 *   leading space — so raw `[S#]` markers never show, even mid-stream.
 */
export const insertInlineCitations = (text: string, references?: Reference[]): string => {
  if (!text) return text
  // Hide an in-progress marker at the streaming frontier (e.g. `[S1` before its
  // closing bracket has streamed in) so it never flashes as raw text — the
  // leading space is kept so the resolved link still reads naturally.
  const withoutPartial = text.replace(/\[S\d*$/, '')
  const byId = new Map((references ?? []).filter(ref => ref.id).map(ref => [ref.id, ref]))
  const numberById = new Map<string, number>()

  return withoutPartial.replace(INLINE_CITATION_RE, (match, id) => {
    const ref = byId.get(id)
    if (!ref?.url) return ''
    let number = numberById.get(id)
    if (number === undefined) {
      number = numberById.size + 1
      numberById.set(id, number)
    }
    const leadingSpace = match.startsWith(' ') ? ' ' : ''
    return `${leadingSpace}[${number}](${ref.url})`
  })
}

// Inline image marker the backend tools tag an image with, e.g. `[IMG3]` — the
// image twin of the `[S#]` citation marker. A leading space is captured so it's
// dropped with an orphan tag but kept when the tag resolves.
const INLINE_IMAGE_TAG_RE = / ?\[(IMG\d+)\]/g

/**
 * Resolve inline `[IMG#]` markers into markdown images (`![alt](url)`) using the
 * turn's `images` map (streamed ahead of the answer tokens, like `references`).
 * The model only ever echoes the short tag; the URL is filled in here
 * deterministically. Mirrors the backend's persist-time resolution so live and
 * reloaded answers agree. A tag with no matching entry — or any tag seen before
 * the map arrives — is dropped, so a raw `[IMG#]` never shows. The resolved
 * `/api/assets/...` URL is then rendered by AnswerText's img handler via the
 * authed AssetImage fetch.
 */
export const resolveImageTags = (text: string, images?: ImageTag[]): string => {
  if (!text) return text
  // Hide an in-progress tag at the streaming frontier (`[IMG` / `[IMG1` before
  // its closing bracket streams in) so it never flashes as raw text.
  const withoutPartial = text.replace(/\[IMG\d*$/, '')
  const byId = new Map(
    (images ?? []).filter(img => img.id && img.url).map(img => [img.id, img]),
  )
  return withoutPartial.replace(INLINE_IMAGE_TAG_RE, (match, id) => {
    const img = byId.get(id)
    if (!img) return ''
    const leadingSpace = match.startsWith(' ') ? ' ' : ''
    return `${leadingSpace}![${img.alt ?? ''}](${img.url})`
  })
}

export interface CitedReference extends Reference {
  // Position in the answer, by order of first appearance — matches the inline
  // `[n]` marker.
  number: number
}

// Matches an inline citation in either form: the raw `[S1]` marker (live
// stream, before resolution) or the resolved `[1](url)` link (persisted answer
// on reload). Group 1 = raw id number; groups 2/3 = resolved number/url.
const CITED_REFERENCE_RE = /\[S(\d+)\]|\[(\d+)\]\(([^)\s]+)\)/g

/**
 * Extract the references actually cited inline in the answer, in order of first
 * appearance, each numbered to match its inline `[n]` marker. Deduped by URL
 * (a source cited twice appears once). Works on both the live (`[S#]`) and
 * reloaded (`[n](url)`) content forms, so the footer list stays consistent.
 *
 * Returns [] when nothing is cited — the footer then renders nothing.
 */
export const getCitedReferences = (text?: string, references?: Reference[]): CitedReference[] => {
  if (!text || !references?.length) return []
  const byId = new Map(references.filter(ref => ref.id).map(ref => [ref.id, ref]))
  const byUrl = new Map(references.filter(ref => ref.url).map(ref => [ref.url, ref]))

  const cited: CitedReference[] = []
  const seenUrls = new Set<string>()
  let match: RegExpExecArray | null
  CITED_REFERENCE_RE.lastIndex = 0
  while ((match = CITED_REFERENCE_RE.exec(text)) !== null) {
    const ref = match[1] !== undefined ? byId.get(`S${match[1]}`) : byUrl.get(match[3])
    if (!ref?.url || seenUrls.has(ref.url)) continue
    seenUrls.add(ref.url)
    cited.push({ ...ref, number: cited.length + 1 })
  }
  return cited
}

export const formatReferencesMarkdown = (references: Reference[]): string => {
  if (!references.length) return ''

  const sourcesSection = references
    .map(ref => {
      const displayTitle = ref.title || extractTitleFromUrl(ref.url)
      return `**Bron:** [${displayTitle}](${ref.url})`
    })
    .join('\n\n')

  const videosWithUrls = references.filter(ref => ref.video_url && ref.video_url.trim() !== '')

  const videosSection =
    videosWithUrls.length > 0
      ? '\n\n' +
        videosWithUrls
          .map(ref => {
            const videoUrl = ref.video_url || ''
            const embedUrl = videoUrl.replace(
              /(?:https?:\/\/)?(?:www\.)?(?:youtube\.com\/watch\?v=|youtu\.be\/)([a-zA-Z0-9_-]+)/,
              'https://www.youtube.com/embed/$1',
            )
            return embedUrl
          })
          .join('\n')
      : ''

  return `\n\n${sourcesSection}${videosSection}\n`
}

// Agent descriptions are authored as markdown (rendered on the chat empty
// state) but also surface in places that can only show plain text: the
// library card and the header tooltip. Reduce to readable prose there rather
// than leaking raw `**` and `- ` markers.
export function markdownToPlainText(markdown: string): string {
  return markdown
    .replace(/```[\s\S]*?```/g, '')
    .replace(/`([^`]*)`/g, '$1')
    .replace(/!\[([^\]]*)\]\([^)]*\)/g, '$1')
    .replace(/\[([^\]]*)\]\([^)]*\)/g, '$1')
    .replace(/^\s{0,3}#{1,6}\s+/gm, '')
    .replace(/^\s{0,3}>\s?/gm, '')
    .replace(/^\s*[-*+]\s+/gm, '')
    .replace(/^\s*\d+\.\s+/gm, '')
    .replace(/(\*\*|__)(.*?)\1/g, '$2')
    .replace(/(\*|_)(.*?)\1/g, '$2')
    .replace(/~~(.*?)~~/g, '$1')
    .replace(/^\s{0,3}([-*_])(\s*\1){2,}\s*$/gm, '')
    .replace(/\n{2,}/g, '\n')
    .trim()
}
